import { useQuery } from "@apollo/client";
import { CompanyContainer } from "components/company/v3/CompanyContainers";
import BrazilianRealEstateFundContext from "components/company/v3/context/BrazilianRealEstateFundContext";
import CompanyNavbarLayout from "components/company/v3/layouts/CompanyNavbarLayout";
import { CompanyContextEnum } from "components/company/v3/menu/enums";
import FiiIssuerSeo from "components/company/v3/seo/FiiIssuerSeo";
import LocaleContext from "components/i18n/LocaleContext";
import NavbarLayout from "components/layouts/NavbarLayout";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import Redirect from "lib/http/Redirect";
import SetHttpStatusCode from "lib/http/SetHttpStatusCode";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import getBaseUrl from "lib/utils/getBaseUrl";
import NotFound from "pages/404";
import { ISecurityCountry } from "types/graphqlTypes";

interface FiisPageLayoutProps {
  tickerSymbol: string;
  children: React.ReactNode;
}

export default function FiisPageLayout({ tickerSymbol, children }: FiisPageLayoutProps) {
  const {
    data: currentData,
    loading,
    previousData,
  } = useQuery<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>(IssuerByTickerSymbolQuery, {
    variables: {
      tickerSymbol,
      country: ISecurityCountry.Br,
    },
    skip: !tickerSymbol || tickerSymbol.length > 8 || /^([0-9]+)$/.test(tickerSymbol),
  });

  const data = currentData || previousData;

  if (loading) {
    return (
      <NavbarLayout>
        <LocaleContext.Provider value="BRA">
          <div className="flex flex-col gap-4 pb-6">
            <LoadingSkeleton count={1} className="h-16 w-full rounded-none" />

            <CompanyContainer>
              <LoadingSkeleton count={1} className="h-96 xl:h-company-box" />
            </CompanyContainer>
          </div>
        </LocaleContext.Provider>
      </NavbarLayout>
    );
  }

  if (!data || !data.issuerByTickerSymbol) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  if (data.issuerByTickerSymbol.__typename !== "BrazilianRealEstateFund") {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  if (tickerSymbol.length === 4) {
    return (
      <Redirect httpStatus={301} to={`${getBaseUrl()}/fiis/${data.issuerByTickerSymbol.tickerSymbol}`.toLowerCase()} />
    );
  }

  return (
    <NavbarLayout>
      <LocaleContext.Provider value="BRA">
        <BrazilianRealEstateFundContext.Provider value={data.issuerByTickerSymbol}>
          <CompanyNavbarLayout context={CompanyContextEnum.FIIs} tickerSymbol={tickerSymbol}>
            <FiiIssuerSeo />
            {children}
          </CompanyNavbarLayout>
        </BrazilianRealEstateFundContext.Provider>
      </LocaleContext.Provider>
    </NavbarLayout>
  );
}
